import classnames from 'classnames';
import omit from "omit.js";
import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {WebUI, getNid} from "../../wui-core/src/index";
import {WithConfigConsumer} from "../../wui-provider/src/context";
import CheckboxGroup from './CheckboxGroup';
import { CheckboxProps } from './iCheckbox.js';
import Icon from "../../wui-icon/src/Icon";

const noop = () => {}


const Checkbox = forwardRef<any, CheckboxProps>((props, ref) => {
    const {
        disabled,
        readOnly,
        inverse,
        colors = 'primary',
        className,
        indeterminate,
        children,
        checked: propChecked,
        clsPrefix,
        style,
        fieldid,
        title,
        value,
        optionType,
        defaultChecked,
        onClick,
        onDoubleClick,
        onChange = () => {},
        antd,
        size,
        browser,
        ...others
    } = props;

    // 优化：区分受控和非受控模式，避免 useEffect 导致的双重渲染
    const isControlled = 'checked' in props && propChecked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false);
    const [focusHover, setFocusHover] = useState({ focused: false, hovered: false });

    // 受控模式直接使用 propChecked，非受控模式使用内部状态
    const checked = isControlled ? !!propChecked : internalChecked;

    // 合并状态对象，供 useImperativeHandle 使用
    const state = {
        checked,
        focused: focusHover.focused,
        hovered: focusHover.hovered
    };

    const doubleClickFlag = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // 组件卸载时清理定时器，防止内存泄漏
    useEffect(() => {
        return () => {
            if (doubleClickFlag.current) {
                clearTimeout(doubleClickFlag.current);
                doubleClickFlag.current = null;
            }
        };
    }, []);

    const change = useCallback((newChecked: boolean, event: any) => {
        // 非受控模式下更新内部状态
        if (!isControlled) {
            setInternalChecked(newChecked);
        }

        if (onChange instanceof Function) {
            if (antd) {
                event.target.checked = newChecked;
                onChange(event, newChecked);
            } else {
                onChange(newChecked, event);
            }
        }

        if (onClick instanceof Function) {
            event.target.checked = newChecked;
            onClick(event);
        }
    }, [isControlled, onChange, onClick, antd]);

    const changeState = useCallback((e: any) => {
        if (e?.target?.tagName !== 'INPUT') {
            e.stopPropagation();
            return false;
        }

        doubleClickFlag.current && clearTimeout(doubleClickFlag.current);

        if (onDoubleClick instanceof Function) {
            doubleClickFlag.current = setTimeout(() => {
                change(!checked, e);
            }, 300);
        } else {
            change(!checked, e);
        }

        if (!antd) {
            e.stopPropagation();
        }
    }, [checked, onDoubleClick, change, antd]);

    const handledbClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        doubleClickFlag.current && clearTimeout(doubleClickFlag.current);
        onDoubleClick && onDoubleClick(checked, e);
    }, [onDoubleClick, checked]);

    const changeInputHovered = useCallback((event: React.MouseEvent<HTMLInputElement>, newHovered: boolean) => {
        if (event.target && (event.target as HTMLInputElement).type === 'checkbox') {
            setFocusHover(prev => ({ ...prev, hovered: newHovered }));
        }
    }, []);

    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>, newFocused: boolean) => {
        if (event.target && event.target.type === 'checkbox') {
            setFocusHover(prev => ({ ...prev, focused: newFocused }));
        }
    }, []);

    // 使用useMemo缓存事件处理函数，避免每次渲染时重新创建
    const isNoEventHandler = disabled || readOnly || browser;
    const eventHandlers = useMemo(() => ({
        mouseEnter: isNoEventHandler ? noop : (event: React.MouseEvent<HTMLInputElement>) => changeInputHovered(event, true),
        mouseLeave: isNoEventHandler ? noop : (event: React.MouseEvent<HTMLInputElement>) => changeInputHovered(event, false),
        focus: isNoEventHandler ? noop : (event: React.FocusEvent<HTMLInputElement>) => handleFocus(event, true),
        blur: isNoEventHandler ? noop : (event: React.FocusEvent<HTMLInputElement>) => handleFocus(event, false)
    }), [isNoEventHandler, changeInputHovered, handleFocus]);
    const inputFieldidProp = fieldid ? { fieldid: `${fieldid}_checkbox` } : {};
    let valueProp = typeof value === 'boolean' ? String(value) : value;

    // 使用 useImperativeHandle 暴露与类组件相同的接口
    useImperativeHandle(ref, () => {
        // 返回一个包含所有必要属性和方法的对象，模拟类组件实例
        // 使用 getter 来实现动态获取最新的值，而不是在依赖数组中包含它们
        return {
            // 使用 getter 动态获取最新的 props
            get props() {
                return props;
            },
            // 使用 getter 动态获取最新的 state
            get state() {
                return state;
            },
            // 暴露方法 - 这些方法由于使用了 useCallback，引用是稳定的
            changeState: changeState,
            change: change,
            handledbClick: handledbClick,
            changeInputHovered: changeInputHovered,
            handleFocus: handleFocus,
            // 暴露 refs
            get doubleClickFlag() {
                return doubleClickFlag.current;
            },
            // 如果需要访问真实的 input DOM 元素
            get input() {
                return inputRef.current;
            }
        };
    }, [changeState, change, handledbClick, changeInputHovered, handleFocus]);
    const input = (
        <input
            {...omit(others, ["antd", "onClick", "onDoubleClick", "onChange", "size"] as any)}
            ref={inputRef}
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={noop}
            onFocus={eventHandlers.focus}
            onBlur={eventHandlers.blur}
            onMouseEnter={eventHandlers.mouseEnter}
            onMouseLeave={eventHandlers.mouseLeave}
            readOnly={readOnly || browser}
            value={valueProp}
            {...inputFieldidProp}
        />
    );

    const buttonCheck = optionType === 'button' && checked ? (
        <span className={`${clsPrefix}-checked`}>
            <Icon type="uf-xuanzhong" />
        </span>
    ) : null;

    // 使用useMemo缓存类名计算结果，避免每次渲染都重新计算
    const classNames = useMemo(() => {
        let classes = {
            [`${clsPrefix}-focused`]: focusHover.focused,
            [`${clsPrefix}-hovered`]: focusHover.hovered,
            'is-checked': checked,
            disabled,
            [`${clsPrefix}-read-only`]: readOnly || browser,
            [`${clsPrefix}-browser`]: browser,
        };

        if (inverse) {
            classes[`${clsPrefix}-inverse`] = true;
        }

        if (colors) {
            classes[`${clsPrefix}-${colors}`] = true;
        }

        if (!propChecked && indeterminate) {
            classes[`${clsPrefix}-indeterminate`] = true;
        }

        if (size && size !== 'default') {
            classes[`${clsPrefix}-${size}`] = true;
        }

        return classnames(clsPrefix, classes);
    }, [clsPrefix, focusHover.focused, focusHover.hovered, checked, disabled, readOnly, browser, inverse, colors, propChecked, indeterminate, size]);
    const fieldidProp = fieldid ? { fieldid } : {};
    const titleProp = title ? { title } : {};
    let adapterNid = getNid(props);

    return (
        <label
            className={classnames(classNames, className)}
            onDoubleClick={isNoEventHandler ? noop : handledbClick}
            style={style}
            onClick={isNoEventHandler ? noop : changeState}
            {...fieldidProp}
            {...adapterNid}
        >
            {input}
            <span className={clsPrefix + '-label'} {...titleProp}>{children}</span>
            {buttonCheck}
        </label>
    );
});

const CheckboxWithDecorators = WithConfigConsumer()(
    WebUI({name: "checkbox"})(Checkbox)
);

CheckboxWithDecorators.Group = CheckboxGroup;

export default CheckboxWithDecorators;
